/**
 * 主机端剪贴板轮询（PC → SPA 历史）。
 *
 * **作用域绑定 FloatingMirror 生命周期**：
 *
 *   - 仅当传入的 `serial` 不为空（=> 镜像已挂载）才启动定时器；
 *   - 浏览器最小化 / 切到别的 tab / 失去焦点时立刻暂停；
 *   - 镜像 unmount → hook 自然 cleanup → 定时器清理。
 *
 * 这条策略来自 plan 的关键决策：「不开任何镜像时不捕获主机剪贴板」，
 * 避免无场景背景偷读用户 PC 剪贴板。
 *
 * 失败 / 拒绝路径：
 *
 *   - `navigator.clipboard.readText` 在 HTTPS / localhost 之外的页面
 *     会直接抛 `NotAllowedError`；首次调用还会向用户弹权限。
 *   - 我们捕获所有错误并把"拒绝/失败"挂在返回的 `permission` 状态上，
 *     UI 抽屉打开时可以提示用户重试授权。
 *   - 拒绝后 hook 会指数退避（最长 30s）—— 持续 1.5s 轮询会让 Chrome
 *     一直把 promise 标红，体验差且占 CPU。
 */
import { useEffect, useState } from 'react';
import { useMyPrefsStore } from '@/lib/myPrefs';
import { pushEntry, useClipboardLastSeenAt } from './store';

/**
 * Default poll interval. 1500 ms tries to balance "feels instant"
 * with "doesn't burn CPU" — Chrome batches the underlying readText
 * call but a tighter cadence still shows up in profilers.
 */
const POLL_INTERVAL_MS = 1500;
/** Backoff caps when readText keeps failing (denied / no API / bug). */
const BACKOFF_INITIAL_MS = 5_000;
const BACKOFF_MAX_MS = 30_000;

export type HostClipboardPermission = 'granted' | 'denied' | 'unsupported' | 'pending';

export interface UseHostClipboardPollResult {
  permission: HostClipboardPermission;
  /** Last error message from `readText`, if the user wants to surface it. */
  lastError: string | null;
}

export function useHostClipboardPoll(
  serial: string | null | undefined,
): UseHostClipboardPollResult {
  const lastSeenAt = useClipboardLastSeenAt(serial);
  const [state, setState] = useState<UseHostClipboardPollResult>({
    permission: 'pending',
    lastError: null,
  });

  // Track the last text we pushed (per-mount) so a noisy poll stream
  // doesn't repeatedly write an unchanged clipboard. The store also
  // dedupes, but failing fast here saves prefs PUT pressure and keeps
  // `lastSeenAt` from drifting on every tick.
  //
  // CRITICAL: we DO NOT close over a `useClipboardActions(serial)` bag
  // here. That bag is rebuilt on every render — its `push` callback's
  // `useCallback` deps include the current `entries` snapshot, so a
  // closure captured at mount time keeps shipping `pushEntry([], ...)`
  // on every tick, silently overwriting the store with each new copy.
  // Instead we read the latest entries imperatively from the store
  // (see `useMyPrefsStore.getState()`), which always sees the freshest
  // snapshot regardless of when this effect was set up.
  useEffect(() => {
    if (!serial) {
      setState({ permission: 'pending', lastError: null });
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) {
      setState({ permission: 'unsupported', lastError: null });
      return;
    }

    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastText: string | null = null;
    let backoff = 0;

    const recordHostText = (text: string): void => {
      // Read the latest entries straight from the prefs store on each
      // tick — bypasses the React closure entirely.
      const prefs = useMyPrefsStore.getState();
      const cur = prefs.devices[serial]?.clipboard?.entries ?? [];
      // Skip if the most-recent host entry already matches; only the
      // host source is consulted so a same-text device echo doesn't
      // suppress a fresh host copy.
      if (cur.some((e) => e.source === 'host' && e.text === text)) return;
      const next = pushEntry(cur, { text, source: 'host' });
      prefs.updateDevice(serial, {
        clipboard: { entries: next, lastSeenAt: Date.now() },
      });
    };

    const tick = async (): Promise<void> => {
      if (stopped) return;
      // Visibility / focus gate. Skipping the read while hidden keeps
      // Chrome from queuing the prompt off-screen + saves CPU.
      if (
        typeof document !== 'undefined' &&
        (document.visibilityState !== 'visible' || !document.hasFocus())
      ) {
        schedule(POLL_INTERVAL_MS);
        return;
      }
      try {
        const text = await navigator.clipboard.readText();
        if (stopped) return;
        backoff = 0;
        if (state.permission !== 'granted') {
          setState({ permission: 'granted', lastError: null });
        }
        if (typeof text === 'string' && text.length > 0 && text !== lastText) {
          lastText = text;
          recordHostText(text);
        }
      } catch (err) {
        if (stopped) return;
        const msg = (err as Error).message ?? 'unknown';
        // Bump backoff so a denied poll doesn't hammer the browser
        // every 1.5 s. Capped so transient errors still recover.
        backoff = backoff === 0 ? BACKOFF_INITIAL_MS : Math.min(backoff * 2, BACKOFF_MAX_MS);
        setState({ permission: 'denied', lastError: msg });
      }
      if (!stopped) schedule(backoff > 0 ? backoff : POLL_INTERVAL_MS);
    };

    const schedule = (delay: number): void => {
      if (stopped) return;
      timer = setTimeout(() => {
        timer = null;
        void tick();
      }, delay);
    };

    // Kick off immediately on mount so the first frame after the
    // mirror opens with whatever's already on the clipboard.
    void tick();

    return () => {
      stopped = true;
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
    };
    // Effect re-spawns ONLY when `serial` changes. We intentionally
    // skip `state.permission` (it's only read for the granted-flip set)
    // because rerunning would re-mount the timer on every write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serial]);

  // Touch lastSeenAt so this hook re-renders when the store updates;
  // not used directly, but keeps the React Profiler graph honest.
  void lastSeenAt;

  return state;
}
